import React, { ReactNode } from 'react';
import {
  CheckboxProps,
  FormControlProps,
  FormGroupProps,
  TextFieldProps as MuiTextFieldProps,
  RadioGroupProps as MuiRadioGroupProps,
  RadioProps,
  SelectProps as MuiSelectProps,
} from '@mui/material';
import { Select } from '../Select/Select';
import { CheckboxGroup } from '../CheckboxGroup/CheckboxGroup';
import { TextArea } from '../TextArea/TextArea';
import { TextField } from '../TextField/TextField';
import { DateField } from '../DateField/DateField';
import { RadioGroup } from '../RadioGroup/RadioGroup';
import { PhoneInput } from '../PhoneInput/PhoneInput';

interface SelectProps
  extends Omit<MuiSelectProps, 'children' | 'options' | 'label' | 'id' | 'value'> {
  id: string;
  label?: string;
  value: string;
  options: Record<string, any>[];
  optionLabelKey?: 'label' | string;
  optionValueKey?: 'value' | string;
  helperText?: string;
}

interface CheckboxOption {
  value: string;
  label: ReactNode;
  isChecked: boolean;
  isDisabled?: boolean;
  labelId?: string;
  checkboxId?: string;
  children?: ReactNode;
}

interface CheckboxGroupProps {
  options: CheckboxOption[];
  onToggle: (updatedOptions: CheckboxOption[]) => void;
  name: string;
  slotProps: {
    formGroup?: FormGroupProps;
    formControl: FormControlProps & { 'aria-labelledby': string };
    checkbox?: CheckboxProps;
  };
}

type TextFieldProps = MuiTextFieldProps & {
  id: string;
};

interface RadioOption {
  value?: string;
  label?: ReactNode;
  isDisabled?: boolean;
  children?: ReactNode;
}

interface RadioGroupProps extends MuiRadioGroupProps {
  selectedOption?: RadioOption;
  options: RadioOption[];
  onOptionSelected: (option: RadioOption) => void;
  slotProps?: {
    radioControl?: RadioProps;
  };
}

type DynamicFormFieldType =
  | 'textbox'
  | 'textarea'
  | 'radio'
  | 'date'
  | 'checkbox'
  | 'select'
  | 'phone';

interface PhoneInputProps extends Omit<MuiTextFieldProps, 'onChange'> {
  id: string;
  onChange?: (value: string) => void;
}

interface BaseDynamicFormFieldProps<T extends DynamicFormFieldType = DynamicFormFieldType> {
  type: T;
}

type DynamicFormFieldTextboxProps = TextFieldProps & BaseDynamicFormFieldProps<'textbox'>;

type DynamicFormFieldTextAreaProps = MuiTextFieldProps & BaseDynamicFormFieldProps<'textarea'>;

interface DynamicFormFieldRadioProps extends RadioGroupProps, BaseDynamicFormFieldProps<'radio'> {}

type DynamicFormFieldDateProps = MuiTextFieldProps & BaseDynamicFormFieldProps<'date'>;

interface DynamicFormFieldCheckboxProps
  extends CheckboxGroupProps,
    BaseDynamicFormFieldProps<'checkbox'> {}

type DynamicFormFieldSelectProps = SelectProps & BaseDynamicFormFieldProps<'select'>;

type DynamicFormFieldPhoneProps = PhoneInputProps & BaseDynamicFormFieldProps<'phone'>;

type DynamicFormFieldProps =
  | DynamicFormFieldTextboxProps
  | DynamicFormFieldTextAreaProps
  | DynamicFormFieldRadioProps
  | DynamicFormFieldDateProps
  | DynamicFormFieldCheckboxProps
  | DynamicFormFieldSelectProps
  | DynamicFormFieldPhoneProps;

export const DynamicFormField: React.FC<DynamicFormFieldProps> = (props) => {
  const { type } = props;

  switch (type) {
    case 'textbox': {
      const { type: fieldType, ...textFieldProps } = props;
      return <TextField {...textFieldProps} />;
    }

    case 'textarea': {
      const { type: fieldType, ...textAreaProps } = props;
      return <TextArea {...textAreaProps} />;
    }

    case 'radio': {
      const { type: fieldType, ...radioGroupProps } = props;
      return <RadioGroup {...radioGroupProps} />;
    }

    case 'date': {
      const { type: fieldType, ...dateFieldProps } = props;
      return <DateField {...dateFieldProps} />;
    }

    case 'checkbox': {
      const { type: fieldType, ...checkboxGroupProps } = props;

      return <CheckboxGroup {...checkboxGroupProps} />;
    }

    case 'select': {
      const { type: fieldType, ...selectProps } = props;

      return <Select {...selectProps} />;
    }

    case 'phone': {
      const { type: fieldType, ...phoneInputProps } = props;

      return <PhoneInput {...phoneInputProps} />;
    }

    default:
      return null;
  }
};
